import React, { useState, useRef, useEffect } from 'react';
import { Menu, Bell, User, Check, CheckCheck, Sun, Moon } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';

interface DashboardHeaderProps {
  onMenuClick: () => void;
  isSidebarOpen: boolean;
}

type Notification = {
  id: string | number;
  message: string;
  type?: string;
  is_read: boolean;
  created_at: string;
  [key: string]: any;
};

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onMenuClick,
  isSidebarOpen,
}) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  } = useNotifications();

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target as Node)
      ) {
        setIsNotificationOpen(false);
      }
    };
    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }
  }, [isNotificationOpen]);

  const handleNotificationClick = async () => {
    const willOpen = !isNotificationOpen;
    setIsNotificationOpen(willOpen);
    if (willOpen) {
      try {
        await fetchNotifications();
      } catch (e) {
        console.error('Failed to fetch notifications:', e);
      }
    }
  };

  const handleMarkAsRead = (id: string | number) => markAsRead(Number(id));

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const formatTime = (created: string) => {
    const diff = Date.now() - new Date(created).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    

    if (mins < 1) return 'Just now';
    if (hrs < 1) return `${mins}m ago`;
    if (days < 1) return `${hrs}h ago`;
    return `${days}d ago`;

  };

  return (
    <header className="ap-dashboard-header">
      <div className="ap-dashboard-header-content">
        <button
          onClick={onMenuClick}
          className="ap-dashboard-menu-btn"
          aria-label="Toggle sidebar"
          aria-controls="dashboard-sidebar"
          aria-expanded={isSidebarOpen}
        >
          <Menu className="ap-dashboard-menu-icon" />
        </button>

        {/* Every dashboard page already renders its own title/icon/subtitle
            just below this bar, so this used to repeat the same name a
            second time. Left empty (rather than removed) so it keeps
            spacing the menu button away from the right-hand icons via its
            flex: 1. */}
        <div className="ap-dashboard-header-title" />

        <div className="ap-dashboard-header-actions">
          {/* ----- Theme toggle ----- */}
          <button
            className="ap-dashboard-theme-btn"
            onClick={toggleTheme}
            aria-label={
              theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'
            }
            title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          >
            {theme === 'light' ? (
              <Moon className="ap-dashboard-theme-icon" />
            ) : (
              <Sun className="ap-dashboard-theme-icon" />
            )}
          </button>

          {/* ----- Notifications ----- */}
          <div
            className="ap-dashboard-notification-container"
            ref={notificationRef}
          >
            <button
              className={`ap-dashboard-notification-btn ${
                isNotificationOpen ? 'active' : ''
              }`}
              onClick={handleNotificationClick}
              aria-label="Notifications"
              aria-expanded={isNotificationOpen}
            >
              <Bell className="ap-dashboard-notification-icon" />
              {unreadCount > 0 && (
                <span className="ap-dashboard-notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {isNotificationOpen && (
              <div className="ap-notification-dropdown">
                <div className="ap-notification-header">
                  <div className="ap-notification-title">
                    <h3>Notifications</h3>
                    {notifications.length > 0 && (
                      <span className="ap-notification-count">
                        {notifications.length}
                      </span>
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <button
                      className="ap-notification-markall-btn"
                      onClick={handleMarkAllAsRead}
                    >
                      <CheckCheck size={14} />
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="ap-notification-list">
                  {notifications.length === 0 ? (
                    <div className="ap-notification-empty">
                      <Bell className="ap-notification-empty-icon" />
                      <p>You're all caught up</p>
                      <span>No new notifications</span>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className="ap-notification-item unread"
                        onClick={() => handleMarkAsRead(n.id)}
                        title="Click to mark as read"
                      >
                        <span className="ap-notification-dot" />
                        <div className="ap-notification-content">
                          <div className="ap-notification-message">
                            {n.message}
                          </div>
                          <div className="ap-notification-meta">
                            {n.type && (
                              <span className="ap-notification-type">
                                {n.type.replace(/_/g, ' ')}
                              </span>
                            )}
                            <span className="ap-notification-time">
                              {formatTime(n.created_at)}
                            </span>
                          </div>
                        </div>
                        <button
                          className="ap-notification-toggle-btn"
                          aria-label="Mark as read"
                          title="Mark as read"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(n.id);
                          }}
                        >
                          <Check size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ----- User menu ----- */}
          <div className="ap-dashboard-user-menu">
            <div className="ap-dashboard-user-info">
              <div className="ap-dashboard-user-name">
                {user?.personName || "User"}
              </div>
            </div>
            <div className="ap-dashboard-user-avatar">
              <User className="ap-dashboard-user-icon" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
